/* eslint-disable no-plusplus */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Stack,
  Typography,
  Chip,
  LinearProgress,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { MailCheck, Eye, TrendingUp, Euro, ShieldAlert, BellOff, BarChart3 } from "lucide-react";
import { collection, documentId, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { db } from "../../../../../../firebase.config";

// ---------- Brand
const BRAND = "#5E17EB";

// ---------- Helpers
const nf = (n, opt = {}) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2, ...opt }).format(n ?? 0);
const pct = (v) => `${nf(v)}%`;
const rate = (num, den) => (den ? (num / den) * 100 : 0);

// yyyy-mm-dd (UTC) pour docId
const toKey = (d) => {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
// label dd/MM local
const toLabel = (d) => d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
// label depuis la clé yyyy-mm-dd
const labelFromKey = (key) => {
  const [y, m, d] = key.split("-").map((x) => parseInt(x, 10));
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
};

// tableau dates UTC inclusif
const rangeFrom = (startUTC, endUTC) => {
  const out = [];
  const d = new Date(startUTC.getTime());
  while (d.getTime() <= endUTC.getTime()) {
    out.push(new Date(d.getTime()));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
};

// ---------- UI: Toggle “pill” moderne
const Pills = styled(ToggleButtonGroup)(({ theme }) => ({
  background: "#f6f5ff",
  borderRadius: 999,
  padding: 4,
  "& .MuiToggleButtonGroup-grouped": {
    border: 0,
    margin: 2,
    borderRadius: 999,
    textTransform: "none",
    padding: "6px 12px",
    "&.Mui-selected": {
      backgroundColor: BRAND,
      color: "#fff",
      boxShadow: "0 4px 14px rgba(94,23,235,0.3)",
      "&:hover": { backgroundColor: BRAND },
    },
    "&:not(.Mui-selected):hover": {
      backgroundColor: "rgba(94,23,235,0.08)",
    },
  },
}));

// ---------- KPI card
function KPICard({ icon: Icon, label, value, suffix, helper, alert }) {
  return (
    <Card
      sx={{
        height: "100%",
        boxShadow: 0,
        borderRadius: 2.5,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        border: "1px solid #5e17eb1e",
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center">
          <Icon size={16} />
          <Typography variant="subtitle2">{label}</Typography>
          {alert && <Chip size="small" color="error" label="Alerte" sx={{ ml: "auto" }} />}
        </Stack>
        <Typography variant="h4" sx={{ mt: 1 }}>
          {nf(value)}
          {suffix || ""}
        </Typography>
        {helper && (
          <Typography variant="caption" color="text.secondary">
            {helper}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function PerfsV2({ campaignId }) {
  // periods: '7j' | 'mois' | 'tous'
  const [period, setPeriod] = useState("7j");
  const [loading, setLoading] = useState(false);
  const [dailyRows, setDailyRows] = useState([]); // [{dateKey, ...}]

  // ---- Lecture Firestore selon période ----
  useEffect(() => {
    if (!campaignId) return;
    setLoading(true);

    const todayUTC = new Date(Date.UTC(
      new Date().getUTCFullYear(),
      new Date().getUTCMonth(),
      new Date().getUTCDate()
    ));

    const coll = collection(db, `campagnes/${campaignId}/daily`);
    let unsub = () => {};

    if (period === "tous") {
      // Pas de bornes : on prend tout, trié par docId
      const qRef = query(coll, orderBy(documentId()));
      unsub = onSnapshot(
        qRef,
        (snap) => {
          const rows = snap.docs.map((d) => {
            const raw = d.data() || {};
            return {
              dateKey: d.id,
              dateLabel: labelFromKey(d.id),
              requests: raw.requests || 0,
              delivered: raw.delivered || 0,
              unique_opens: raw.unique_opens || 0,
              unique_clicks: raw.unique_clicks || raw.clicked || 0,
              revenue: raw.revenue || 0,
              hard_bounces: (raw.hard_bounces || 0) + (raw.dropped || 0),
              unsubscribes: (raw.unsubscribes || 0) + (raw.group_unsubscribes || 0),
            };
          });
          setDailyRows(rows);
          setLoading(false);
        },
        (err) => {
          console.error("Firestore daily error (tous):", err);
          setLoading(false);
        }
      );
    } else {
      // Génère l’intervalle UTC
      let startUTC;
      if (period === "7j") {
        // 7 jours glissants (aujourd’hui inclus)
        startUTC = new Date(todayUTC.getTime());
        startUTC.setUTCDate(startUTC.getUTCDate() - 6);
      } else {
        // 'mois' → du 1er du mois courant à aujourd’hui
        startUTC = new Date(Date.UTC(todayUTC.getUTCFullYear(), todayUTC.getUTCMonth(), 1));
      }
      const endUTC = todayUTC;

      const daysArr = rangeFrom(startUTC, endUTC);
      const startKey = toKey(daysArr[0]);
      const endKey = toKey(daysArr[daysArr.length - 1]);

      const qRef = query(
        coll,
        where(documentId(), ">=", startKey),
        where(documentId(), "<=", endKey),
        orderBy(documentId())
      );

      unsub = onSnapshot(
        qRef,
        (snap) => {
          const byId = new Map();
          snap.forEach((d) => byId.set(d.id, d.data()));

          const rows = daysArr.map((d) => {
            const k = toKey(d);
            const label = toLabel(d);
            const raw = byId.get(k) || {};
            return {
              dateKey: k,
              dateLabel: label,
              requests: raw.requests || 0,
              delivered: raw.delivered || 0,
              unique_opens: raw.unique_opens || 0,
              unique_clicks: raw.unique_clicks || raw.clicked || 0,
              revenue: raw.revenue || 0,
              hard_bounces: (raw.hard_bounces || 0) + (raw.dropped || 0),
              unsubscribes: (raw.unsubscribes || 0) + (raw.group_unsubscribes || 0),
            };
          });

          setDailyRows(rows);
          setLoading(false);
        },
        (err) => {
          console.error("Firestore daily error (period):", err);
          setLoading(false);
        }
      );
    }

    return () => unsub();
  }, [campaignId, period]);

  // ---- Agrégats période (KPI) ----
  const agg = useMemo(() => {
    const sum = (k) => dailyRows.reduce((a, b) => a + (b[k] || 0), 0);
    const delivered = sum("delivered");
    const requests = sum("requests") || Math.round(delivered * 1.02);
    const uniqueOpens = sum("unique_opens");
    const uniqueClicks = sum("unique_clicks");
    const revenue = sum("revenue");
    const hardB = sum("hard_bounces");
    const unsub = sum("unsubscribes");

    return {
      delivered,
      requests,
      uniqueOpens,
      uniqueClicks,
      revenue,
      deliveredRate: rate(delivered, requests),
      openRate: rate(uniqueOpens, delivered),
      ctr: rate(uniqueClicks, delivered),
      ctor: rate(uniqueClicks, uniqueOpens),
      hardBounceRate: rate(hardB, requests),
      unsubscribeRate: rate(unsub, delivered),
    };
  }, [dailyRows]);

  // ---- Charts
  const categories = dailyRows.map((r) => r.dateLabel);

  const lineOptions = {
    chart: { id: "evolution", toolbar: { show: false }, fontFamily: "Inter, system-ui, sans-serif" },
    stroke: { width: 3, curve: "smooth" },
    dataLabels: { enabled: false },
    grid: { strokeDashArray: 4 },
    xaxis: { categories },
    yaxis: [
      { title: { text: "Comptes" }, labels: { formatter: (v) => nf(v) } },
      { opposite: true, max: 100, tickAmount: 4, title: { text: "Taux (%)" }, labels: { formatter: (v) => `${nf(v)}%` } },
    ],
    tooltip: { shared: true, intersect: false },
    legend: { position: "top" },
  };
  const lineSeries = [
    { name: "Délivrés", type: "line", data: dailyRows.map((r) => r.delivered), yAxisIndex: 0 },
    { name: "Taux d’ouverture", type: "line", data: dailyRows.map((r) => rate(r.unique_opens, r.delivered)), yAxisIndex: 1 },
    { name: "Clics (U)", type: "line", data: dailyRows.map((r) => r.unique_clicks), yAxisIndex: 0 },
  ];

  const areaOptions = {
    chart: { id: "revenue", toolbar: { show: false }, fontFamily: "Inter, system-ui, sans-serif" },
    stroke: { curve: "smooth", width: 2 },
    dataLabels: { enabled: false },
    fill: { type: "gradient", gradient: { shadeIntensity: 0.3, opacityFrom: 0.6, opacityTo: 0.05 } },
    grid: { strokeDashArray: 4 },
    xaxis: { categories },
    yaxis: [{ labels: { formatter: (v) => `${nf(v)} €` } }],
    tooltip: { y: { formatter: (v) => `${nf(v)} €` } },
  };
  const areaSeries = [{ name: "Revenu par jour", data: dailyRows.map((r) => r.revenue) }];

  const alerts = { hard: agg.hardBounceRate > 1.5 };

  return (
    <Box paddingBottom={10} sx={{ width: "100%" }} padding={2}>
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Header */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <BarChart3 color={BRAND} size={18} />
          <Typography variant="h6">Performance</Typography>
        </Stack>

        <Pills
          size="small"
          exclusive
          value={period}
          onChange={(_, v) => v && setPeriod(v)}
        >
          <ToggleButton value="7j">7 j</ToggleButton>
          <ToggleButton value="mois">Mois</ToggleButton>
          <ToggleButton value="tous">Tous</ToggleButton>
        </Pills>
      </Stack>

      {/* KPIs */}
      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
          <KPICard icon={MailCheck} label="Taux de délivrabilité" value={agg.deliveredRate} suffix="%" helper={`${nf(agg.delivered)} / ${nf(agg.requests)} délivrés`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
          <KPICard icon={Eye} label="Taux d'ouverture (unique)" value={agg.openRate} suffix="%" helper={`CTOR ${pct(agg.ctor)}`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
          <KPICard icon={TrendingUp} label="CTR (unique)" value={agg.ctr} suffix="%" helper={`${nf(agg.uniqueClicks)} clics uniques`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
          <KPICard icon={Euro} label="Revenu" value={agg.revenue} suffix=" €" helper={`RPE ${agg.delivered ? nf(agg.revenue / agg.delivered) : 0} € / email`} />
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }} />

      {/* KPI secondaires + Chart 1 */}
      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} sm={6} md={6} sx={{ display: "flex" }}>
          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} sm={6} md={6} sx={{ display: "flex" }}>
              <KPICard icon={ShieldAlert} label="Taux de hard bounce" value={agg.hardBounceRate} suffix="%" helper="Période" alert={alerts.hard} />
            </Grid>
            <Grid item xs={12} sm={6} md={6} sx={{ display: "flex" }}>
              <KPICard icon={BellOff} label="Taux de désabonnement" value={agg.unsubscribeRate} suffix="%" helper="Période" />
            </Grid>
            <Grid item xs={12} md={12}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Évolution quotidienne — Délivrés / Taux d’ouverture / Clics (U)
                  </Typography>
                  <Chart options={lineOptions} series={lineSeries} type="line" height={200} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Chart 2 */}
        <Grid item xs={12} sm={6} md={6} sx={{ display: "flex" }}>
          <Card sx={{ height: "100%", width: "100%" }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Revenu par jour
              </Typography>
              <Chart options={areaOptions} series={areaSeries} type="area" height={340} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }} />
      <Divider sx={{ mt: 3, mb: 1 }} />
      <Typography variant="caption" color="text.secondary">
        * Branché Firestore (daily). “Tous” affiche tout l’historique, “Mois” = 1er jour → aujourd’hui, “7 j” = glissant.
      </Typography>
    </Box>
  );
}
